// CommandPalette — ⌘K / Ctrl+K: jump to any page the operator may open, or find a document, customer or
// account (task 355.4). Replaces the top-bar GlobalSearch autocomplete.
//
// Pages come from the same navigation model as the sidebar (so permission gates match); records come from
// the existing search endpoint (useSearch, ≥ 2 characters, 300 ms debounce). Filtering is done here, not by
// cmdk, because server results are already filtered.

import React, { useEffect, useMemo, useState } from 'react';
import { Modal, Spin } from 'antd';
import { Command } from 'cmdk';
import { ArrowRight, FileText, Landmark, Users } from 'lucide-react';
import { useNavigate } from 'react-router-dom';
import { useSearch } from '../../hooks/useSearch';
import type { SearchResultDto } from '../../../api/generated/schema';
import { ROUTES } from '../../utils/constants';
import { StatusPill } from '../ui';
import { flattenDestinations, matchDestinations } from './navModel';
import { useNavGroups } from './useNavGroups';
import styles from './CommandPalette.module.css';

const ENTITY_LABELS: Record<string, string> = {
  Document: 'Documents',
  Customer: 'Customers',
  Account: 'Accounts',
};

const ENTITY_ICONS: Record<string, typeof FileText> = {
  Document: FileText,
  Customer: Users,
  Account: Landmark,
};

/** Where a hit opens (task 528: the api sends no url; a document hit's secondaryText is its type code). */
function hitRoute(r: SearchResultDto): string {
  switch (r.entityType) {
    case 'Document':
      return ROUTES.documents.detail(r.secondaryText ?? '', r.entityId);
    case 'Customer':
      return ROUTES.customers.detail(r.entityId);
    default:
      return `/gl/accounts/${r.entityId}`;
  }
}

interface CommandPaletteProps {
  open: boolean;
  onClose: () => void;
}

export default function CommandPalette({ open, onClose }: CommandPaletteProps): React.ReactElement {
  const navigate = useNavigate();
  const { groups } = useNavGroups();
  const [query, setQuery] = useState('');
  const [debounced, setDebounced] = useState('');

  useEffect(() => {
    const timer = setTimeout(() => setDebounced(query.trim()), 300);
    return () => clearTimeout(timer);
  }, [query]);

  // Start fresh each time the palette opens.
  useEffect(() => {
    if (open) {
      setQuery('');
      setDebounced('');
    }
  }, [open]);

  const destinations = useMemo(() => flattenDestinations(groups), [groups]);
  const pages = useMemo(() => matchDestinations(destinations, query), [destinations, query]);

  const { data, isFetching } = useSearch(debounced);
  const results = useMemo(
    () => (debounced.length >= 2 ? (data?.items ?? []) : []),
    [data?.items, debounced],
  );
  const resultGroups = useMemo(() => {
    const grouped = new Map<string, SearchResultDto[]>();
    for (const r of results) grouped.set(r.entityType, [...(grouped.get(r.entityType) ?? []), r]);
    return [...grouped.entries()];
  }, [results]);

  const go = (path: string) => {
    onClose();
    navigate(path);
  };

  const searching = debounced.length >= 2 && isFetching;

  return (
    <Modal
      open={open}
      onCancel={onClose}
      footer={null}
      closable={false}
      width={640}
      destroyOnHidden
      styles={{ content: { padding: 0 }, body: { padding: 0 } }}
      title={null}
    >
      <Command label="Command palette" shouldFilter={false} className={styles.command} loop>
        <div className={styles.inputRow}>
          <Command.Input
            value={query}
            onValueChange={setQuery}
            placeholder="Search documents, customers, accounts — or jump to a page"
            className={styles.input}
            autoFocus
          />
          {searching && <Spin size="small" />}
        </div>
        <Command.List className={styles.list}>
          {!searching && pages.length === 0 && results.length === 0 && (
            <Command.Empty className={styles.empty}>
              {query.trim().length < 2
                ? 'No matching page.'
                : 'Nothing found. Try a document number, name or IBAN.'}
            </Command.Empty>
          )}

          {pages.length > 0 && (
            <Command.Group heading="Go to" className={styles.group}>
              {pages.map((d) => {
                const Icon = d.icon;
                return (
                  <Command.Item
                    key={d.path}
                    value={`page:${d.path}`}
                    onSelect={() => go(d.path)}
                    className={styles.item}
                  >
                    <span className={styles.itemIcon}>
                      {Icon ? <Icon size={16} strokeWidth={1.7} /> : <ArrowRight size={16} />}
                    </span>
                    <span className={styles.itemMain}>{d.label}</span>
                    <span className={styles.itemMeta}>{d.group}</span>
                  </Command.Item>
                );
              })}
            </Command.Group>
          )}

          {resultGroups.map(([entityType, items]) => {
            const Icon = ENTITY_ICONS[entityType] ?? FileText;
            return (
              <Command.Group
                key={entityType}
                heading={ENTITY_LABELS[entityType] ?? entityType}
                className={styles.group}
              >
                {items.map((r) => (
                  <Command.Item
                    key={`${entityType}:${r.entityId}`}
                    value={`${entityType}:${r.entityId}`}
                    onSelect={() => go(hitRoute(r))}
                    className={styles.item}
                  >
                    <span className={styles.itemIcon}>
                      <Icon size={16} strokeWidth={1.7} />
                    </span>
                    <span className={styles.itemMain}>
                      <span className={styles.itemTitle}>{r.displayText}</span>
                      {r.secondaryText && <span className={styles.itemSub}>{r.secondaryText}</span>}
                    </span>
                    {r.status && <StatusPill status={r.status} size="small" />}
                  </Command.Item>
                ))}
              </Command.Group>
            );
          })}
        </Command.List>
        <div className={styles.footer}>
          <span>
            <kbd className={styles.kbd}>↑</kbd>
            <kbd className={styles.kbd}>↓</kbd> move
          </span>
          <span>
            <kbd className={styles.kbd}>↵</kbd> open
          </span>
          <span>
            <kbd className={styles.kbd}>esc</kbd> close
          </span>
        </div>
      </Command>
    </Modal>
  );
}
